import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';
import { TextLink } from '@/components/TextLink';
import type { Breadcrumb } from '@/utils/breadcrumbs';

type BreadcrumbsProps = {
  breadcrumbs: Breadcrumb[];
  className?: string;
};

// Shows the breadcrumbs that are not hidden (the hidden ones are in the schema only). A trail with only Home is not
// worth showing. All the links are grey, but the last one is dark (as in the design)
export const Breadcrumbs = ({ breadcrumbs, className }: BreadcrumbsProps) => {
  const visibleBreadcrumbs = breadcrumbs.filter(({ hidden }) => !hidden);

  if (visibleBreadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className={twMerge('text-xs', className)} data-automation="ZA.breadcrumbs" aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-x-2">
        {visibleBreadcrumbs.map(({ label, href }, index) => {
          const isLast = index === visibleBreadcrumbs.length - 1;

          return (
            <Fragment key={href}>
              {index > 0 ? (
                <li role="presentation" aria-hidden="true">
                  &gt;
                </li>
              ) : null}

              <li>
                <TextLink className={isLast ? undefined : 'text-base-400'} href={href}>
                  {label.trim()}
                </TextLink>
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

// The breadcrumbs of a page, placed above the footer and aligned with it
export const PageBreadcrumbs = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => (
  <Breadcrumbs className="mx-auto max-w-[540px] px-4 py-7 md:max-w-[1224px]" breadcrumbs={breadcrumbs} />
);
